/* ==========================================================================
   <sura-product>
   The add-to-cart form is a real Shopify product form, so it works without
   JavaScript. This element adds the gallery, quantity stepper, Buy Now,
   the pincode check and the sticky mobile buy bar.
   ========================================================================== */

class SuraProduct extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form[action*="/cart/add"]');
    this.errorEl = this.querySelector('[data-pdp-error]');

    this.initGallery();
    this.initQuantity();
    this.initBuyNow();
    this.initPincode();
    this.initStickyBar();
  }

  disconnectedCallback() {
    if (this.stickyObserver) this.stickyObserver.disconnect();
  }

  /* --------------------------------------------------------------- Gallery */

  initGallery() {
    this.slides = Array.from(this.querySelectorAll('[data-pdp-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-pdp-thumb]'));
    if (this.thumbs.length === 0) return;

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.showMedia(thumb.dataset.mediaId));
    });
  }

  showMedia(id) {
    this.slides.forEach((slide) => {
      slide.classList.toggle('is-active', slide.dataset.mediaId === id);
    });
    this.thumbs.forEach((thumb) => {
      thumb.classList.toggle('is-active', thumb.dataset.mediaId === id);
    });
  }

  /* -------------------------------------------------------------- Quantity */

  initQuantity() {
    this.qtyInput = this.querySelector('[data-pdp-qty]');
    const down = this.querySelector('[data-pdp-qty-down]');
    const up = this.querySelector('[data-pdp-qty-up]');
    if (!this.qtyInput) return;

    if (down) down.addEventListener('click', () => this.stepQuantity(-1));
    if (up) up.addEventListener('click', () => this.stepQuantity(1));

    // Guard against pasted or typed nonsense
    this.qtyInput.addEventListener('change', () => {
      const value = parseInt(this.qtyInput.value, 10);
      if (Number.isNaN(value) || value < 1) this.qtyInput.value = 1;
    });
  }

  stepQuantity(delta) {
    const current = parseInt(this.qtyInput.value, 10) || 1;
    this.qtyInput.value = Math.max(1, current + delta);
  }

  /* --------------------------------------------------------------- Buy now */

  initBuyNow() {
    // There can be two: the one in the buy box and the one in the sticky bar.
    this.buyButtons = Array.from(this.querySelectorAll('[data-pdp-buy]'));
    if (this.buyButtons.length === 0 || !this.form) return;

    this.buyButtons.forEach((button) => {
      button.addEventListener('click', () => this.buyNow());
    });
  }

  async buyNow() {
    const variantInput = this.querySelector('[data-pdp-variant]');
    if (!variantInput) return;

    this.setBuyDisabled(true);
    this.clearError();

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantInput.value),
              quantity: parseInt(this.qtyInput?.value, 10) || 1,
            },
          ],
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Shopify returns a readable reason, e.g. not enough stock
        this.showError(data.description || data.message || 'Could not add this item.');
        this.setBuyDisabled(false);
        return;
      }

      window.location.href = `${window.Shopify.routes.root}checkout`;
    } catch (error) {
      this.showError('Something went wrong. Please try again.');
      this.setBuyDisabled(false);
    }
  }

  setBuyDisabled(state) {
    this.buyButtons.forEach((button) => {
      button.disabled = state;
    });
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  clearError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }

  /* ------------------------------------------------------------ Sticky bar */

  initStickyBar() {
    this.stickyBar = this.querySelector('[data-pdp-sticky]');
    const actions = this.querySelector('.sura-pdp__actions');
    if (!this.stickyBar) return;

    if (!actions || !('IntersectionObserver' in window)) {
      // Without a reference point, a bar that is always there beats no bar.
      this.stickyBar.classList.add('is-visible');
      return;
    }

    // Show it only once the real buttons have scrolled away, so the two
    // never sit on screen together.
    this.stickyObserver = new IntersectionObserver(
      (entries) => {
        this.stickyBar.classList.toggle('is-visible', !entries[0].isIntersecting);
      },
      { threshold: 0 }
    );

    this.stickyObserver.observe(actions);
  }

  /* --------------------------------------------------------------- Pincode */

  initPincode() {
    this.pincodeInput = this.querySelector('[data-pdp-pincode]');
    this.pincodeResult = this.querySelector('[data-pdp-pincode-result]');
    const checkBtn = this.querySelector('[data-pdp-pincode-check]');
    if (!this.pincodeInput || !checkBtn || !this.pincodeResult) return;

    checkBtn.addEventListener('click', () => this.checkPincode());
    this.pincodeInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.checkPincode();
      }
    });
  }

  checkPincode() {
    const value = this.pincodeInput.value.trim();

    if (!/^\d{6}$/.test(value)) {
      this.setPincodeResult('Enter a valid 6-digit pincode.', false);
      return;
    }

    const raw = this.pincodeResult.dataset.serviceable || '';
    const list = raw
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);

    // No list configured means every pincode is treated as serviceable
    const serviceable =
      list.length === 0 ||
      list.some((entry) => (entry.length < 6 ? value.startsWith(entry) : entry === value));

    this.setPincodeResult(
      serviceable ? this.pincodeResult.dataset.ok : this.pincodeResult.dataset.fail,
      serviceable
    );
  }

  setPincodeResult(message, ok) {
    this.pincodeResult.textContent = message;
    this.pincodeResult.hidden = false;
    this.pincodeResult.classList.toggle('is-ok', ok);
    this.pincodeResult.classList.toggle('is-fail', !ok);
  }
}

if (!customElements.get('sura-product')) {
  customElements.define('sura-product', SuraProduct);
}
